import { some } from "lodash";
import {
  AbstractDate,
  Interval,
  Panels,
  PeriodId,
  PeriodMath,
  PeriodType,
  Range,
  getPeriodId,
} from "@linkfluence/models/date";
import { Milestone, getMilestone } from "@linkfluence/models/milestone";
import { Timezone } from "@linkfluence/models/timezone";
import {
  endOf,
  getRangeFromDates,
  isSame,
  next,
  startOf,
} from "@tootools/date";

type Period = Omit<Range, "interval">;
type Timestamp = { prev: AbstractDate; next: AbstractDate };

export const getPanel = (period?: PeriodId, milestone?: Milestone) => {
  if (period === PeriodId.Custom_From) {
    return Panels.CalendarFrom;
  }
  if (period === PeriodId.Custom) {
    return !!milestone ? Panels.Milestones : Panels.Calendar;
  }
  if (some(PeriodMath, { type: PeriodType.Rolling, id: period })) {
    return Panels.Rolling;
  }
  return undefined;
};

export const getCurrentPeriod = (period: Period) => {
  if (period) {
    return getPeriodId(period.from, period.to);
  }
  return undefined;
};

export const getCurrentMilestone = (
  currentPeriod: PeriodId | undefined,
  prevPeriod: Period,
  milestones: Milestone[]
) => {
  if (currentPeriod === PeriodId.Custom) {
    return getMilestone(prevPeriod.from, prevPeriod.to, milestones);
  }
  return undefined;
};

const isSameFromDate = (from: Timestamp): boolean =>
  isSame(from.prev, from.next);

const isSametoDate = (to: Timestamp): boolean => isSame(to.prev, to.next);

const isFromToRangeEqual = (from: Timestamp, to: Timestamp): boolean => {
  return isSameFromDate(from) && isSametoDate(to);
};

export const isApplyButtonDisabled = (
  isNewCalendar: boolean,
  allowFromOnly: boolean,
  from: Timestamp,
  to: Timestamp
): boolean => {
  if (isNewCalendar) return false;
  if (allowFromOnly && isSameFromDate(from)) return true;
  if (isFromToRangeEqual(from, to)) return true;
  return false;
};

export const getPeriodFromDates = (
  from: AbstractDate,
  to: AbstractDate,
  timezone: Timezone
) => {
  const period = getRangeFromDates(from, to, timezone);
  if (to === "now") {
    if (period.interval === Interval.Hour && from !== "now/d") {
      return {
        ...period,
        to: next(startOf(period.to, Interval.Hour), 1, Interval.Hour),
      };
    }
    return {
      ...period,
      to: endOf(period.to, Interval.Day),
    };
  }
  return period;
};
